/**
 * Custom hook for managing chat history
 * Provides state management and API integration for chat operations with infinite scroll support
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { chatService, type ChatQueryParams } from "../services/chat-service";
import { useChatEvents } from "./use-chat-events";
import type {
  ChatHistoryApiItem,
  GroupedChatData,
  ChatPagination,
} from "@taqat/core";

/**
 * Hook options for customizing behavior
 */
interface UseChatHistoryOptions {
  /** Auto-fetch chat history on mount */
  autoFetch?: boolean;
  /** Initial query parameters */
  initialParams?: ChatQueryParams;
  /** Page limit for pagination */
  limit?: number;
  /**
   * Scope the history to a single agent.
   *
   * Filtering client-side would be lossy — the workspace list is paginated,
   * so an agent's older chats may simply not be on the fetched page. This
   * routes to the agent-scoped endpoint instead, which paginates correctly.
   */
  agentId?: string;
}

/**
 * Chat history list state
 */
interface ChatHistoryListState {
  conversations: ChatHistoryApiItem[];
  groupedData: GroupedChatData | null;
  pagination: ChatPagination | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
}

/**
 * Loading state for different operations
 */
interface ChatHistoryLoadingState {
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
}

/**
 * Return type for useChatHistory hook
 */
interface UseChatHistoryReturn {
  /** Current chat history state */
  state: ChatHistoryListState;
  /** Loading state for different operations */
  loading: ChatHistoryLoadingState;
  /** Fetch chat history with optional parameters */
  fetchChatHistory: (params?: ChatQueryParams) => Promise<void>;
  /** Refresh current chat history */
  refresh: () => Promise<void>;
  /** Load more conversations (pagination) */
  loadMore: () => Promise<void>;
  /** Delete a chat conversation */
  deleteChat: (chatId: string) => Promise<void>;
  /** Reset state to initial values */
  reset: () => void;
  /** Check if more data is available */
  hasMore: boolean;

  // Backward compatibility - these can be removed later
  data: ChatHistoryApiItem[];
  groupedData: GroupedChatData | null;
  pagination: ChatPagination | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook for managing chat history list and operations with infinite scroll support
 */
export function useChatHistory(
  options: UseChatHistoryOptions = {}
): UseChatHistoryReturn {
  const { autoFetch = true, initialParams = {}, limit = 25, agentId } = options;
  
  // Chat events for emitting deletion events
  const chatEvents = useChatEvents();

  // Main state
  const [state, setState] = useState<ChatHistoryListState>({
    conversations: [],
    groupedData: null,
    pagination: null,
    loading: false,
    error: null,
    searchQuery: "",
  });

  // Loading state for operations
  const [loading, setLoading] = useState<ChatHistoryLoadingState>({
    loading: false,
    loadingMore: false,
    error: null,
  });

  // Current query parameters
  const [currentParams, setCurrentParams] = useState<ChatQueryParams>({
    ...initialParams,
    limit,
  });

  // Track current page for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const initialLoadDone = useRef(false);

  /**
   * Normalizes grouped data to ensure all groups exist
   */
  const normalizeGroupedData = useCallback((rawData: any): GroupedChatData => {
    return {
      today: rawData?.today || [],
      yesterday: rawData?.yesterday || [],
      lastWeek: rawData?.lastWeek || [],
      lastMonth: rawData?.lastMonth || [],
      older: rawData?.older || [],
    };
  }, []);

  /**
   * Flattens grouped data into a single array
   */
  const flattenGroupedData = useCallback(
    (groupedData: GroupedChatData): ChatHistoryApiItem[] => {
      return [
        ...groupedData.today,
        ...groupedData.yesterday,
        ...groupedData.lastWeek,
        ...groupedData.lastMonth,
        ...groupedData.older,
      ];
    },
    []
  );

  /**
   * Fetches chat history from API
   */
  const fetchChatHistory = useCallback(
    async (params?: ChatQueryParams): Promise<void> => {
      const queryParams = { ...currentParams, ...params };

      // Is this the initial load (page 1) or a pagination "load more"?
      const isInitialLoad = (queryParams.page || 1) === 1;

      // Only flip the full-screen loading flag for the INITIAL load. During
      // pagination loadMore() owns `loadingMore` (the small bottom skeleton);
      // if we also set the global `loading` here, the sidebar swaps the whole
      // list for skeletons, which collapses the scroll container and snaps the
      // user back to the top — so they can never scroll to the end. Keep the
      // existing list mounted while the next page streams in.
      if (isInitialLoad) {
        setLoading((prev) => ({ ...prev, loading: true, error: null }));
        setState((prev) => ({ ...prev, loading: true, error: null }));
      } else {
        setLoading((prev) => ({ ...prev, error: null }));
      }

      try {
        const response = agentId
          ? await chatService.getAgentChatHistory(agentId, queryParams)
          : await chatService.getChatHistory(queryParams);

        if (response.success && response.data) {
          const normalizedGroupedData = normalizeGroupedData(response.data);
          const flatData = flattenGroupedData(normalizedGroupedData);

          if (isInitialLoad) {
            // Replace all data for initial load
            setState((prev) => ({
              ...prev,
              conversations: flatData,
              groupedData: normalizedGroupedData,
              pagination: response.pagination || null,
              loading: false,
              error: null,
            }));
            initialLoadDone.current = true;
          } else {
            // Merge data for pagination
            setState((prev) => {
              const existingIds = new Set(
                prev.conversations.map((item) => item.id)
              );
              const newItems = flatData.filter(
                (item) => !existingIds.has(item.id)
              );

              // Merge grouped data
              const mergeGroup = (
                prevGroup: ChatHistoryApiItem[],
                currGroup: ChatHistoryApiItem[]
              ) => {
                const prevIds = new Set(prevGroup.map((item) => item.id));
                const newGroupItems = currGroup.filter(
                  (item) => !prevIds.has(item.id)
                );
                return [...prevGroup, ...newGroupItems];
              };

              const mergedGroupedData: GroupedChatData = prev.groupedData
                ? {
                  today: mergeGroup(
                    prev.groupedData.today,
                    normalizedGroupedData.today
                  ),
                  yesterday: mergeGroup(
                    prev.groupedData.yesterday,
                    normalizedGroupedData.yesterday
                  ),
                  lastWeek: mergeGroup(
                    prev.groupedData.lastWeek,
                    normalizedGroupedData.lastWeek
                  ),
                  lastMonth: mergeGroup(
                    prev.groupedData.lastMonth,
                    normalizedGroupedData.lastMonth
                  ),
                  older: mergeGroup(
                    prev.groupedData.older,
                    normalizedGroupedData.older
                  ),
                }
                : normalizedGroupedData;

              return {
                ...prev,
                conversations: [...prev.conversations, ...newItems],
                groupedData: mergedGroupedData,
                pagination: response.pagination || null,
                loading: false,
                error: null,
              };
            });
          }

          setLoading((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: null,
          }));
        } else {
          throw new Error(response.message || "Failed to fetch chat history");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch chat history";

        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));

        setLoading((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          error: errorMessage,
        }));
      }
    },
    [agentId, currentParams, normalizeGroupedData, flattenGroupedData]
  );

  /**
   * Refreshes the current chat history
   */
  const refresh = useCallback(async (): Promise<void> => {
    setCurrentPage(1);
    initialLoadDone.current = false;
    await fetchChatHistory({ ...currentParams, page: 1 });
  }, [fetchChatHistory, currentParams]);

  /**
   * Loads more conversations for pagination
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (
      !state.pagination?.hasNextPage ||
      loading.loadingMore ||
      loading.loading
    ) {
      return;
    }

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setLoading((prev) => ({ ...prev, loadingMore: true }));

    await fetchChatHistory({ ...currentParams, page: nextPage });
  }, [
    state.pagination,
    loading.loadingMore,
    loading.loading,
    currentPage,
    fetchChatHistory,
    currentParams,
  ]);

  /**
   * Deletes a chat conversation and removes it from the local state
   */
  const deleteChat = useCallback(async (chatId: string): Promise<void> => {
    try {
      // Call the delete API
      await chatService.deleteChat(chatId);

      // Emit delete-chat event to notify other components
      chatEvents.emit("delete-chat", chatId);

      // Remove the chat from local state
      setState((prev) => {
        // Remove from flat conversations array
        const updatedConversations = prev.conversations.filter(
          (chat) => chat.id !== chatId
        );

        // Remove from grouped data
        let updatedGroupedData: GroupedChatData | null = null;
        if (prev.groupedData) {
          updatedGroupedData = {
            today: prev.groupedData.today.filter((chat) => chat.id !== chatId),
            yesterday: prev.groupedData.yesterday.filter(
              (chat) => chat.id !== chatId
            ),
            lastWeek: prev.groupedData.lastWeek.filter(
              (chat) => chat.id !== chatId
            ),
            lastMonth: prev.groupedData.lastMonth.filter(
              (chat) => chat.id !== chatId
            ),
            older: prev.groupedData.older.filter((chat) => chat.id !== chatId),
          };
        }

        // Update pagination total count
        const updatedPagination = prev.pagination
          ? { ...prev.pagination, total: prev.pagination.total - 1 }
          : null;

        return {
          ...prev,
          conversations: updatedConversations,
          groupedData: updatedGroupedData,
          pagination: updatedPagination,
        };
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete chat";

      setLoading((prev) => ({
        ...prev,
        error: errorMessage,
      }));

      // Re-throw error so calling component can handle it if needed
      throw error;
    }
  }, [chatEvents]);

  /**
   * Resets state to initial values
   */
  const reset = useCallback((): void => {
    setState({
      conversations: [],
      groupedData: null,
      pagination: null,
      loading: false,
      error: null,
      searchQuery: "",
    });

    setLoading({
      loading: false,
      loadingMore: false,
      error: null,
    });

    setCurrentParams({ ...initialParams, limit });
    setCurrentPage(1);
    initialLoadDone.current = false;
  }, [initialParams, limit]);

  /**
   * Upserts a chat in the optimistic state.
   * Updates the title in place if the chat exists; otherwise prepends a new
   * "today" entry so the sidebar can show streamed chats without a refetch.
   *
   * An empty `newTitle` is treated as "insert if missing, leave existing
   * title alone" — this lets early lifecycle events (e.g. `stream-start`)
   * register a brand-new chat without clobbering an already-titled one when
   * the same chat receives a follow-up message.
   */
  const upsertChatTitle = useCallback(
    (chatId: string, newTitle: string, agentId?: string | null) => {
      setState((prev) => {
        const exists = prev.conversations.some((chat) => chat.id === chatId);

        if (exists) {
          if (!newTitle) return prev;

          const updatedConversations = prev.conversations.map((chat) =>
            chat.id === chatId ? { ...chat, title: newTitle } : chat
          );

          let updatedGroupedData = prev.groupedData;
          if (prev.groupedData) {
            const updateGroup = (group: ChatHistoryApiItem[]) =>
              group.map((chat) =>
                chat.id === chatId ? { ...chat, title: newTitle } : chat
              );

            updatedGroupedData = {
              today: updateGroup(prev.groupedData.today),
              yesterday: updateGroup(prev.groupedData.yesterday),
              lastWeek: updateGroup(prev.groupedData.lastWeek),
              lastMonth: updateGroup(prev.groupedData.lastMonth),
              older: updateGroup(prev.groupedData.older),
            };
          }

          return {
            ...prev,
            conversations: updatedConversations,
            groupedData: updatedGroupedData,
          };
        }

        const now = new Date().toISOString();
        const optimisticChat = {
          id: chatId,
          title: newTitle,
          llm: "",
          sandboxId: "",
          userId: "",
          workspaceId: "",
          agentId: agentId ?? null,
          createdAt: now,
          updatedAt: now,
        } as ChatHistoryApiItem;

        const baseGroupedData: GroupedChatData =
          prev.groupedData ?? {
            today: [],
            yesterday: [],
            lastWeek: [],
            lastMonth: [],
            older: [],
          };

        return {
          ...prev,
          conversations: [optimisticChat, ...prev.conversations],
          groupedData: {
            ...baseGroupedData,
            today: [optimisticChat, ...baseGroupedData.today],
          },
        };
      });
    },
    []
  );

  /** Backwards-compatible alias used by callers that only update existing chats. */
  const updateChatTitle = useCallback(
    (chatId: string, newTitle: string) => upsertChatTitle(chatId, newTitle),
    [upsertChatTitle]
  );

  // Subscribe to update-title events for optimistic title updates / inserts
  useEffect(() => {
    const unsubscribe = chatEvents.subscribe("update-title", (chatId, data) => {
      const title = data?.title;
      if (!title) return;

      upsertChatTitle(chatId, title, data?.agentId ?? null);
    });
    return unsubscribe;
  }, [chatEvents, upsertChatTitle]);

  // Insert a placeholder row the moment a chat starts streaming, so the
  // sidebar reflects new conversations immediately (matches the agent DM
  // sidebar behavior). Existing rows are left untouched — the empty-title
  // upsert path above is a no-op when a chat is already cached.
  useEffect(() => {
    const unsubscribe = chatEvents.subscribe("stream-start", (chatId, data) => {
      if (data?.teamId) return;
      upsertChatTitle(chatId, "", data?.agentId ?? null);
    });
    return unsubscribe;
  }, [chatEvents, upsertChatTitle]);

  // Auto-fetch on mount - only if no data exists yet
  useEffect(() => {
    if (autoFetch && !initialLoadDone.current && state.conversations.length === 0) {
      fetchChatHistory({ ...initialParams, page: 1 });
    }
  }, []); // Only run on mount

  // Listen for new chat events and refresh history
  // DISABLED: These event listeners were causing flickering by triggering full refreshes
  // on every chat navigation/update. Consider implementing optimistic updates or
  // selective refresh logic instead of full list refresh.
  // useEffect(() => {
  //   const unsubscribeNewChat = chatEvents.subscribe("new-chat", () => {
  //     // Refresh chat history when a new chat is created
  //     if (initialLoadDone.current) {
  //       refresh();
  //     }
  //   });

  //   const unsubscribeUpdateChat = chatEvents.subscribe("update-chat", () => {
  //     // Refresh chat history when a chat is updated (e.g., title change)
  //     if (initialLoadDone.current) {
  //       refresh();
  //     }
  //   });

  //   return () => {
  //     unsubscribeNewChat();
  //     unsubscribeUpdateChat();
  //   };
  // }, [chatEvents, refresh]);

  const hasMore = state.pagination?.hasNextPage || false;

  return {
    state,
    loading,
    fetchChatHistory,
    refresh,
    loadMore,
    deleteChat,
    reset,
    hasMore,

    // Backward compatibility
    data: state.conversations,
    groupedData: state.groupedData,
    pagination: state.pagination,
    isLoading: loading.loading,
    isLoadingMore: loading.loadingMore,
    isError: !!state.error || !!loading.error,
    error: state.error || loading.error,
    refetch: refresh,
  };
}
